import { now } from '@rematter/common/dateTime';
import { AssistantAgentStreamEvent } from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import { assistantChatArchiveMutationAtom } from '../_graphql/mutationAssistantChatArchive';
import { assistantChatMessageSendMutationAtom } from '../_graphql/mutationAssistantChatMessageSend';
import { assistantChatShareMutationAtom } from '../_graphql/mutationAssistantChatShare';
import { assistantChatUnshareMutationAtom } from '../_graphql/mutationAssistantChatUnshare';
import { assistantChatUpdateMutationAtom } from '../_graphql/mutationAssistantChatUpdate';
import {
  assistantChatMessagesQueryAtom,
  type ChatMessageRecord,
} from '../_graphql/queryAssistantChatMessages';
import type { ChatMetadata } from '../_types/chatMetadata';
import type { SentAttachment } from '../types';

import {
  type EventContext,
  handleComplete,
  handleConversationTitle,
  handleError,
  handleMessage,
  handleRetry,
  handleThinking,
  handleToolCall,
  handleToolResult,
  initializeAtomRefs,
} from './chatConversation.handlers';
import { $chats, $updateOptimisticChatTitle } from './chatList.state';

// ========================================================================
// ATOMS
// ========================================================================

// 🔘🔘🔘 PLAIN ATOMS - Sent attachments per message (persists across reloads)
// Key: "chatId:messageContent" → attachment metadata
export const $sentAttachments = atom<Map<string, SentAttachment[]>>(new Map());

// 🔘🔘🔘 PLAIN ATOMS - Selected chat
export const $selectedChatId = atom<string | null>(null);

// 🔘🔘🔘 PLAIN ATOMS - Local message state (single source of truth)
export const $localMessages = atom<ChatMessageRecord[]>([]);

// Subscription state
export const $subscriptionStatus = atom<
  'connected' | 'disconnected' | 'reconnecting'
>('disconnected');

export const $streamingError = atom<{
  error: string;
  errorCode?: string;
} | null>(null);
export const $isAgentWorking = atom<boolean>(false);

// Track expanded tool call blocks (collapsed by default)
export const $expandedToolCallIds = atom<Set<string>>(new Set<string>());
export const $toggleToolCallExpanded = atom(
  null,
  (get, set, toolCallId: string) => {
    const next = new Set(get($expandedToolCallIds));
    if (next.has(toolCallId)) next.delete(toolCallId);
    else next.add(toolCallId);
    set($expandedToolCallIds, next);
  },
);

// 🟢🟢🟢 REMOTE DATA - Messages for selected chat
export const $chatMessages = assistantChatMessagesQueryAtom((get) => {
  const chatId = get($selectedChatId);
  return {
    variables: { chatId: chatId ?? '' },
    skip: !chatId,
  };
});

// 🟡🟡🟡 HIDDEN ACTIONS - Mutations
const $sendMessageMutation = assistantChatMessageSendMutationAtom({
  onSuccess: (_get) => {
    // get($chatMessages).refetch();
  },
});

const $archiveChatMutation = assistantChatArchiveMutationAtom({
  onSuccess: (get, set) => {
    get($chats).refetch();
    set($selectedChatId, null);
  },
});

const $updateChatMutation = assistantChatUpdateMutationAtom({
  onSuccess: (get) => {
    get($chats).refetch();
  },
});

const $shareChatMutation = assistantChatShareMutationAtom({
  onSuccess: (get) => {
    get($chats).refetch();
  },
});

const $unshareChatMutation = assistantChatUnshareMutationAtom({
  onSuccess: (get) => {
    get($chats).refetch();
  },
});

// 🟠🟠🟠 ACTIONS - Public actions
export const $sendMessage = atom(
  null,
  async (
    get,
    set,
    params: {
      content: string;
      fileIds?: string[];
      attachments?: { fileName: string; mimeType: string }[];
      firstName?: string;
      lastName?: string;
      timezone: string;
    },
  ) => {
    const { content, fileIds, attachments, firstName, lastName, timezone } =
      params;
    const chatId = get($selectedChatId);
    if (!chatId) throw new Error('No chat selected');

    // Create user message with temp ID and parts-based structure
    const tempId = `temp-user-${Date.now()}`;
    const parts: ChatMessageRecord['parts'] = [];
    let seq = 0;

    if (content) {
      parts.push({
        id: `${tempId}-part-${seq}`,
        sequence: seq++,
        data: { type: 'Text', text: content, state: 'Done' },
      });
    }

    if (attachments) {
      for (const att of attachments) {
        parts.push({
          id: `${tempId}-part-${seq}`,
          sequence: seq++,
          data: {
            type: 'File',
            filename: att.fileName,
            mediaType: att.mimeType,
            url: '',
          },
        });
      }
    }

    const userMsg: ChatMessageRecord = {
      id: tempId,
      chatId,
      role: 'User',
      parts,
      createdAt: now(),
    };

    if (attachments && attachments.length > 0) {
      const key = `${chatId}:${content}`;
      set($sentAttachments, (prev) => {
        const next = new Map(prev);
        next.set(key, attachments);
        return next;
      });
    }

    set($localMessages, (prev) => [...prev, userMsg]);
    set($isAgentWorking, true);

    // Use passed user data and timezone
    const metadata: ChatMetadata = {
      firstName,
      lastName,
      timezone,
    };

    try {
      const result = await set($sendMessageMutation, {
        chatId,
        content,
        fileIds,
        metadata,
      });

      // Backend returns boolean indicating workflow started successfully
      // The actual message will be delivered via subscription with a real ID
      if (!result?.assistantSendChatMessage) {
        // If workflow didn't start, remove the temp message
        set($isAgentWorking, false);
        set($localMessages, (prev) => prev.filter((m) => m.id !== tempId));
        set($streamingError, { error: 'Failed to start agent workflow' });
        setTimeout(() => set($streamingError, null), 10000);
      }
      // Otherwise, keep the temp message until subscription replaces it
    } catch (error) {
      set($isAgentWorking, false);
      set($localMessages, (prev) => prev.filter((m) => m.id !== tempId));
      const message =
        error instanceof Error
          ? error.message
          : String(error ?? 'Unknown error');
      set($streamingError, { error: message });
      setTimeout(() => set($streamingError, null), 10000);
    }
  },
);

export const $archiveChat = atom(null, async (get, set) => {
  const chatId = get($selectedChatId);
  if (!chatId) throw new Error('No chat selected');

  await set($archiveChatMutation, { id: chatId });
});

export const $archiveChatById = atom(
  null,
  async (get, set, params: { chatId: string }) => {
    const selectedId = get($selectedChatId);
    if (params.chatId === selectedId) {
      set($selectedChatId, null);
    }
    await set($archiveChatMutation, { id: params.chatId });
  },
);

export const $renameChat = atom(
  null,
  async (get, set, params: { title: string }) => {
    const chatId = get($selectedChatId);
    if (!chatId) throw new Error('No chat selected');

    set($updateOptimisticChatTitle, { chatId, title: params.title });
    await set($updateChatMutation, { id: chatId, title: params.title });
  },
);

export const $shareChat = atom(
  null,
  async (_get, set, params: { chatId: string }) => {
    const result = await set($shareChatMutation, { chatId: params.chatId });
    return result?.assistantShareChat ?? null;
  },
);

export const $unshareChat = atom(
  null,
  async (_get, set, params: { chatId: string }) => {
    await set($unshareChatMutation, { chatId: params.chatId });
  },
);

// 🔘🔘🔘 PLAIN ATOMS - Loading state for chat switching
export const $isLoadingChatMessages = atom<boolean>(false);

// 🟠🟠🟠 ACTIONS - Load messages from server
export const $loadMessagesFromServer = atom(null, async (get, set) => {
  // Called when: chat changes, reconnect after error, page refresh
  set($isLoadingChatMessages, true);
  try {
    const { refetch } = get($chatMessages);
    const result = await refetch();

    // Replace local messages with server data
    set($localMessages, result.messages ?? []);
  } finally {
    set($isLoadingChatMessages, false);
  }
});

// Initialize atom refs for handlers (avoids circular dependency)
initializeAtomRefs($streamingError, $isAgentWorking);

// 🟠🟠🟠 ACTIONS - Handle subscription event (REFACTORED)
export const $handleSubscriptionEvent = atom(
  null,
  (get, set, event: AssistantAgentStreamEvent) => {
    const messages = get($localMessages);
    const chatId = get($selectedChatId);
    const metadata = (event.metadata || {}) as Record<string, unknown>;
    const agentLevel = (metadata['agentLevel'] as string) || 'orchestrator';
    const parentToolCallId = metadata['parentToolCallId'] as string | undefined;

    // Build context for handlers
    const context: EventContext = {
      messages,
      chatId,
      agentLevel: agentLevel as 'orchestrator' | 'subagent',
      parentToolCallId,
    };

    // Route to appropriate handler
    switch (event.type) {
      case 'ConversationTitle': {
        handleConversationTitle(event, context, set);
        break;
      }
      case 'Thinking': {
        set($localMessages, handleThinking(event, context));
        break;
      }
      case 'Message': {
        set($localMessages, handleMessage(event, context));
        break;
      }
      case 'ToolCall': {
        set($localMessages, handleToolCall(event, context));
        break;
      }
      case 'ToolResult': {
        set($localMessages, handleToolResult(event, context));
        break;
      }
      case 'Error': {
        if (metadata['retrying']) {
          set($localMessages, handleRetry(context.messages));
        } else {
          handleError(event, set);
        }
        break;
      }
      case 'Complete': {
        set($localMessages, handleComplete(context.messages, set, get));
        break;
      }
    }
  },
);

// 🟠🟠🟠 ACTIONS - Clear local state (on chat change)
export const $clearLocalState = atom(null, (_get, set) => {
  set($localMessages, []);
  set($streamingError, null);
  set($isAgentWorking, false);
});

// 🟢🟢🟢 COMPUTED - All messages (single source of truth)
// Local messages are the single source of truth
// Updated directly by user actions and subscription events
export const $allMessages = atom((get) => get($localMessages));

// 🟢🟢🟢 COMPUTED - Loading states
export const $isSendingMessage = atom(
  (get) => get($sendMessageMutation).isPending,
);

export const $isLoadingMessages = atom((get) => get($chatMessages).isLoading);
